import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Eye, Edit, Trash2, RotateCcw, Wallet, Plus, Building2, Landmark } from 'lucide-react';

const PAYMENT_METHOD_TYPE_LABELS = {
  cash: 'Numerário',
  bank_transfer: 'Transferência Bancária',
  pos: 'POS',
  mobile_money: 'Dinheiro Móvel',
  cheque: 'Cheque',
  other: 'Outro',
};

export const PaymentsHubTab = ({
  paymentMethods,
  paymentAccounts,
  onCreateMethod,
  onCreateAccount,
  onEditMethod,
  onEditAccount,
  onDeleteMethod,
  onDeleteAccount,
  onRestoreMethod,
  onRestoreAccount,
  onViewAccountHistory,
}) => {
  const activeMethods = paymentMethods.filter((item) => !item.is_deleted);
  const activeAccounts = paymentAccounts.filter((item) => !item.is_deleted);

  return (
    <div className="space-y-5" data-testid="payments-hub-tab">
      <div className="grid gap-4 md:grid-cols-3">
        <Card data-testid="payments-hub-summary-methods">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Formas activas</p>
            <p className="mt-2 text-3xl font-semibold">{activeMethods.length}</p>
          </CardContent>
        </Card>
        <Card data-testid="payments-hub-summary-accounts">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Contas activas</p>
            <p className="mt-2 text-3xl font-semibold">{activeAccounts.length}</p>
          </CardContent>
        </Card>
        <Card data-testid="payments-hub-summary-linked">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Contas com método ligado</p>
            <p className="mt-2 text-3xl font-semibold">{activeAccounts.filter((item) => item.payment_method_name).length}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <Card data-testid="payments-hub-methods-card">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4 text-blue-600" />
                Formas de Pagamento
              </CardTitle>
              <CardDescription>Todos os métodos aceites pelo ERP num único painel de gestão.</CardDescription>
            </div>
            <Button onClick={onCreateMethod} data-testid="payments-hub-create-method">
              <Plus className="mr-2 h-4 w-4" />
              Nova forma
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {paymentMethods.length === 0 ? (
              <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                Nenhuma forma de pagamento encontrada.
              </div>
            ) : paymentMethods.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`payments-hub-method-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{item.name}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{item.notes || 'Sem notas adicionais'}</p>
                  </div>
                  <Badge variant={item.is_deleted ? 'destructive' : item.is_active ? 'default' : 'secondary'}>
                    {item.is_deleted ? 'Eliminado' : item.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  <Badge variant="outline" data-testid={`payments-hub-method-type-${item.id}`}>
                    {PAYMENT_METHOD_TYPE_LABELS[item.type] || item.type}
                  </Badge>
                  <div className="flex flex-wrap gap-2">
                    {item.is_deleted ? (
                      <Button size="sm" variant="outline" onClick={() => onRestoreMethod(item)} data-testid={`payments-hub-restore-method-${item.id}`}>
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => onEditMethod(item)} data-testid={`payments-hub-edit-method-${item.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDeleteMethod(item)} data-testid={`payments-hub-delete-method-${item.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="payments-hub-accounts-card">
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet className="h-4 w-4 text-emerald-600" />
                Contas de Pagamento
              </CardTitle>
              <CardDescription>Contas financeiras ligadas às formas de pagamento usadas nos recebimentos.</CardDescription>
            </div>
            <Button onClick={onCreateAccount} data-testid="payments-hub-create-account">
              <Plus className="mr-2 h-4 w-4" />
              Nova conta
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {paymentAccounts.length === 0 ? (
              <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                Nenhuma conta de pagamento encontrada.
              </div>
            ) : paymentAccounts.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid={`payments-hub-account-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{item.name}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{item.payment_method_name || 'Sem forma ligada'}</p>
                  </div>
                  <Badge variant={item.is_deleted ? 'destructive' : item.is_active ? 'default' : 'secondary'}>
                    {item.is_deleted ? 'Eliminado' : item.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                    {item.owner === 'CG Business' ? <Building2 className="h-4 w-4 text-blue-600" /> : <Landmark className="h-4 w-4 text-emerald-600" />}
                    <span>{item.owner}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {item.is_deleted ? (
                      <Button size="sm" variant="outline" onClick={() => onRestoreAccount(item)} data-testid={`payments-hub-restore-account-${item.id}`}>
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => onViewAccountHistory(item)} data-testid={`payments-hub-history-account-${item.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onEditAccount(item)} data-testid={`payments-hub-edit-account-${item.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => onDeleteAccount(item)} data-testid={`payments-hub-delete-account-${item.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};